import React from 'react';
interface AchievementProgressBarProps {
  current: number;
  max: number;
  /** Optional left-aligned label shown on the meta row. */
  label?: React.ReactNode;
  /** "inline" shows a leading label; "labelled" shows a centered "Voortgang" row. */
  layout?: 'inline' | 'labelled';
  size?: 'thin' | 'regular';
  /** Fill colour. "slate" (default, locked progress) or "amber" (positive completion). */
  tone?: 'slate' | 'amber';
  /** Accessible name for the meter. Falls back to a sensible default. */
  ariaLabel?: string;
}
const FILL_TONE: Record<NonNullable<AchievementProgressBarProps['tone']>, string> = {
  slate: 'bg-slate-400',
  amber: 'bg-amber-400'
};
/**
 * AchievementProgressBar — shared progress meter.
 *
 * Previously this exact markup was duplicated across the card variants. It now
 * also powers the section's overall-completion bar. Centralising it keeps the
 * count/track styling consistent and stops the call sites from drifting.
 */
export function AchievementProgressBar({
  current,
  max,
  label,
  layout = 'inline',
  size = 'regular',
  tone = 'slate',
  ariaLabel
}: AchievementProgressBarProps) {
  const safeMax = max > 0 ? max : 1;
  const pct = Math.min(100, Math.round(current / safeMax * 100));
  const trackHeight = size === 'thin' ? 'h-1' : 'h-1.5';
  return <div className="w-full" data-id="element-1762">
      <div className="flex justify-between text-[9px] font-bold text-slate-400 mb-1" data-id="element-1763">
        <span className="truncate" data-id="element-1764">
          {layout === 'labelled' ? 'Voortgang' : label}
        </span>
        <span className="flex-shrink-0 tabular-nums" data-id="element-1765">
          {current} / {max}
        </span>
      </div>
      <div className={`${trackHeight} w-full bg-slate-200 rounded-full overflow-hidden`} role="progressbar" aria-valuenow={current} aria-valuemin={0} aria-valuemax={max} aria-label={ariaLabel} data-id="element-1766">
        <div className={`h-full ${FILL_TONE[tone]} rounded-full transition-all duration-500`} style={{
        width: `${pct}%`
      }} data-id="element-1767" />
      </div>
    </div>;
}